// export  function ApiCall(url , method, body , config, successCb, errorcb )
import allChronic from "./ChronicQuestions.json";

import { toast } from "sonner";
import moment from "moment";
import * as Sentry from "@sentry/react";

type AxiosLikeError = {
  isAxiosError: true;
  message?: string;
  response?: { data?: { message?: string } };
};

// Avoids axios's own `isAxiosError` type guard: this project's outdated
// `@types/axios` package shadows Axios's bundled types and breaks its
// resolution. Axios still sets `isAxiosError = true` on every error it
// throws at runtime regardless of type defs, so this check is reliable.
function isAxiosError(err: unknown): err is AxiosLikeError {
  return (
    typeof err === "object" &&
    err !== null &&
    (err as { isAxiosError?: unknown }).isAxiosError === true
  );
}

export const TOAST_STYLES = {
  success:
    "bg-gradient-to-r from-emerald-500 via-green-500 to-lime-500 text-white border-none shadow-lg shadow-emerald-300/40 rounded-xl",
  error:
    "bg-gradient-to-r from-rose-500 via-red-500 to-orange-500 text-white border-none shadow-lg shadow-rose-300/40 rounded-xl",
};

/** Mongo `_id` used in `/patients/:patientId` and the care-plan routes. */
const PATIENT_ROUTE_ID = /^[a-f0-9]{24}$/i;

const PATIENT_ID_ROUTE_PATTERNS = [
  /^\/patients\/([^/]+)(?:\/(?:edit|device-settings))?$/,
  /^\/careplan\/([^/]+)(?:\/[^/]+)?$/,
  /^\/treatment-plan\/([^/]+)(?:\/[^/]+)?$/,
];

const NON_PATIENT_SEGMENTS = new Set(["add", "opt-out"]);

export function isPatientRouteId(id: string | undefined): boolean {
  return PATIENT_ROUTE_ID.test(id ?? "");
}

/** Patient id from a route that uses a Mongo id. Null for unrelated paths. */
export function patientIdFromPath(pathname: string): string | null {
  for (const pattern of PATIENT_ID_ROUTE_PATTERNS) {
    const match = pathname.match(pattern);
    if (!match) continue;
    let id = match[1];
    try {
      id = decodeURIComponent(id);
    } catch {
      return id;
    }
    if (NON_PATIENT_SEGMENTS.has(id)) return null;
    return id;
  }
  return null;
}

/** Turn "www.google.com" into "https://www.google.com" so <a href> is not relative. */
export function toAbsoluteUrl(url: string) {
  const trimmed = url.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

export function getErrToast(err: unknown) {
  const isAxiosErr = isAxiosError(err);

  const errM =
    typeof err === "string"
      ? err
      : isAxiosErr
        ? err.response?.data?.message || err.message || "Internal server error"
        : err instanceof Error
          ? err.message
          : "Internal server error";

  if (import.meta.env.DEV) {
    console.log("ERROR : ", errM);
  }

  // Axios errors are already reported by the AxiosInstance response
  // interceptor (which has richer context: url/method/status). Only report
  // here as a fallback for non-Axios errors, so the same failure isn't
  // double-counted in Sentry. Plain strings (e.g. RHFWrapper's "Error in
  // form fields ...") are always intentional user-facing validation
  // messages in this codebase's convention, never real bugs — only genuine
  // Error objects (real JS exceptions) get reported.
  const alreadyReported = (err as { __sentryReported?: boolean })?.__sentryReported;
  if (!alreadyReported && !isAxiosErr && err instanceof Error) {
    Sentry.captureException(err);
  }

  toast.error(errM, {
    className: TOAST_STYLES.error,
  });
}

export function getPromiseToast(pr: any) {
  toast.promise(pr, {
    loading: "Working on it...",
    success: (message) => ({
      message: `${message}`,
      className: TOAST_STYLES.success,
    }),
    error: (error) => ({
      message:
        typeof error == "string"
          ? error
          : `${error?.response?.data?.message || error?.message || "Internal Server Error"}`,
      className: TOAST_STYLES.error,
    }),
  });
}

export function getPromise() {
  let resolveFn = (val: any) => { },
    rejectFn = (val: any) => { };

  const pr = new Promise((resolve, reject) => {
    resolveFn = resolve;
    rejectFn = reject;
  });
  return { pr, resolveFn, rejectFn };
}
export function getNpiDataForProviders(data: any, org: any) {
  try {
    // here data ==results?.[0]
    if (!data) return {};
    //  let orgId = org.find((v:any)=> v.organizationName?.toLowerCase() == )
    return {
      npiNumber: data?.number,
      ...(data?.taxonomies && Array.isArray(data?.taxonomies)
        ? { taxonomies: data?.taxonomies.map((d: any) => d?.desc) }
        : {}),

      ...(data?.addresses && Array.isArray(data?.addresses)
        ? {
          street: data?.addresses?.[0]?.address_1,
          city: data?.addresses?.[0]?.city,
          state: data?.addresses?.[0]?.state,
          zipcode:
            data?.addresses?.[0]?.postal_code &&
              typeof data.addresses[0].postal_code == "string"
              ? data.addresses[0].postal_code.substring(0, 5)
              : "",
          ...(data?.addresses?.[0]?.telephone_number
            ? {
              phone: `+1${data?.addresses?.[0]?.telephone_number?.replaceAll("-", "")}`,
            }
            : {}),
        }
        : {}),
      ...(data?.basic
        ? {
          ...(data?.basic?.authorized_official_first_name
            ? { firstName: data?.basic?.authorized_official_first_name }
            : {}),
          ...(data?.basic?.authorized_official_last_name
            ? { lastName: data?.basic?.authorized_official_last_name }
            : {}),
          ...(data?.basic?.authorized_official_telephone_number && {
            phone: `+1${data?.basic?.authorized_official_telephone_number}`,
          }),
          ...(data?.basic?.first_name && {
            firstName: data?.basic?.first_name,
          }),
          ...(data?.basic?.last_name && { lastName: data?.basic?.last_name }),
          ...(data?.basic?.middle_name && {
            middleName: data?.basic?.middle_name,
          }),
          ...(data?.basic?.sex &&
            data?.basic?.sex == "F" && { gender: "female" }),
          ...(data?.basic?.sex &&
            data?.basic?.sex == "M" && { gender: "male" }),
        }
        : {}),
    };
  } catch (error: any) {
    console.log("ERROR IN CONVERTING NPI DATA FOR PROVIDERS", error?.message);
    return {};
  }
}

export function getTableValue(tableValue: any) {
  // 1. If array of strings → join with commas
  if (Array.isArray(tableValue)) {
    if (tableValue.every((v) => typeof v === "string")) {
      return tableValue.join(", ");
    }
  }

  // 2. If number → check if it's a timestamp (large number) or regular number
  if (typeof tableValue === "number") {
    // Only treat as timestamp if it's a large number (milliseconds since epoch)
    // Timestamps are typically > 1000000000000 (year 2001+)
    // Small numbers like minutes (120, 240, etc.) should be displayed as-is
    if (tableValue > 1000000000000) {
      const date = new Date(tableValue);
      if (!isNaN(date.getTime())) {
        return formatDate(date);
      }
    }
    // For regular numbers (minutes, counts, etc.), return as-is
    return tableValue;
  }

  // 3. If ISO date string → format for display
  if (typeof tableValue === "string") {
    const date = new Date(tableValue);

    if (!isNaN(date.getTime())) {
      // check if string is ISO-like (YYYY-MM-DD) or looks like a date
      if (/^\d{4}-\d{2}-\d{2}/.test(tableValue) || /^\d{4}-\d{2}-\d{2}T/.test(tableValue)) {
        // Calendar dates (DOB, etc.): use YYYY-MM-DD from string so UTC midnight does not show previous local day
        const trimmed = tableValue.trim();
        const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(trimmed);
        // DOB-style values stored as midnight UTC (avoid shifting to previous local day)
        const isUtcMidnight =
          /^(\d{4}-\d{2}-\d{2})T00:00:00(\.0+)?(Z|[+-]00:?00)$/i.test(
            trimmed,
          );
        if (isDateOnly || isUtcMidnight) {
          return formatDate(trimmed);
        }
        return formatDate(date);
      }
    }

    // 4. Otherwise return string text as it is (check for phone number)
    return formatUSPhoneNumber(tableValue) || tableValue;
  }

  // 5. For other types return as-is
  return tableValue;
}

// let obj = {
//   role : "Admin",
//   email : 'test@tt.in',
//   password : 'A123456@a',

//   role : "Provider",
//     email : 'jafifit220@agenra.com',
//   password : 'A123456@a',

//   role : "Practioners",
//     email : 'kojajam178@canvect.com',
//   password : 'A123456@a',
// role : "Caretaker",
//.  wokiraf453@agenra.com
//   password : 'A123456@a',

// }

export const CalegoryFlow = ["CONSENT", "CURRENT STATUS","MEDICATIONS","MEDICAL HISTORY" , "Chronic Conditions" , "CONCLUSION"];


export const carePlanQuestions = [
  // multiple_choice, single_choice , single_input, textarea,  multi_input , single_select,  multi_select
  // 1.CONSENT
  {
    question:
      "Please verify that the patient has been informed verbally of the following information:",
    questionrandomid: "cons_001",
    options: [
      "There could be a copay (cost-sharing)",
      "You have the right to refuse/revoke enrollment at any time",
      "Your Plan of Care can be shared with your other providers",
      "Only one provider per month can provide these services to you",
    ],
    category: "CONSENT",
    type: "multiple_choice",
    // hideCommnetSection : true,
    
  },
  // 2.CURRENT STATUS
  {
    question:
      "Currently, how are you doing? Do you have any acute issues you would like to discuss with me today?",
    questionrandomid: "curr_001",
    options: [],
    category: "CURRENT STATUS",
    type: "textarea",
  },
   {
    question: "Do you need any medication refills?",
    questionrandomid: "meds_003",
    options: ["YES", "NO"],
    openCommetonOptions: ["YES"],
    category: "CURRENT STATUS",
    type: "single_choice",
  },
  {
    question: "Do you need any lab work?",
    questionrandomid: "curr_002",
    options: ["YES", "NO"],
    openCommetonOptions: ["YES"],
    category: "CURRENT STATUS",
    type: "single_choice",
  },
  {
    question: "Do you need any referrals?",
    questionrandomid: "curr_003",
    options: ["YES", "NO"],
    openCommetonOptions: ["YES"],
    category: "CURRENT STATUS",
    type: "single_choice",
  },
  {
    question:
      "Do you need any help scheduling an appointment with your provider? I'd be more than happy to assist you with this now",
    questionrandomid: "curr_004",
    options: ["YES", "NO"],
    openCommetonOptions: ["YES"],
    category: "CURRENT STATUS",
    type: "single_choice",
  },
  {
    question:
      "Do you have a good support system? Please let me know of any resources (besides your health care team) you can turn to for help with any health related problems (ex: family, friend, neighbors, religious/spiritual leaders, community center)”",
    questionrandomid: "curr_005",
    options: [],
    category: "CURRENT STATUS",
    type: "textarea",
  },
  {
    question: "Do you live in a safe environment?",
    questionrandomid: "curr_006",
    options: ["YES", "NO"],
    openCommetonOptions: ["NO"],
    category: "CURRENT STATUS",
    type: "single_choice",
  },
  // 3.MEDICAL HISTORY
  {
    question: "Do you have any new allergies?",
    questionrandomid: "medh_001",
    options: ["YES", "NO"],
    openCommetonOptions: ["YES"],
    category: "MEDICAL HISTORY",
    type: "single_choice",
  },
  {
    question: "Have you had any recent procedures or surgeries?",
    questionrandomid: "medh_002",
    options: ["YES", "NO"],
    openCommetonOptions: ["YES"],
    category: "MEDICAL HISTORY",
    type: "single_choice",
  },

  // 4.Medication
  {
    question: "Have you had any recent changes to your medications?",
    questionrandomid: "meds_001",
    options: ["YES", "NO"],
    openCommetonOptions: ["YES"],
    category: "MEDICATIONS",
    type: "single_choice",
  },
  {
    question: "Do you take your medications as prescribed?",
    questionrandomid: "meds_002",
    options: ["YES", "NO"],
    openCommetonOptions: ["NO"],
    category: "MEDICATIONS",
    type: "single_choice",
  },

  // 5.CONCLUSION
  {
    question: "The patient has agreed to the plan of care.",
    questionrandomid: "conc_001",
    options: ["YES", "NO"],
    openCommetonOptions: ["NO"],
    category: "CONCLUSION",
    type: "single_choice",
  },
  {
    question:
      "Are you aware that your healthcare team will have access to your plan of care, 24/7?",
    questionrandomid: "conc_002",
    options: ["YES", "NO"],
    openCommetonOptions: ["NO"],
    category: "CONCLUSION",
    type: "single_choice",
  },
  {
    question:
      "Are you aware that you have access to Care Management Services, 24/7?",
    questionrandomid: "conc_003",
    options: ["YES", "NO"],
    openCommetonOptions: ["NO"],
    category: "CONCLUSION",
    type: "single_choice",
  },
  // {
  //   question:
  //     "We will contact you in approximately one month for a follow up call. Do you have a preferred contact day?",
  //   questionrandomid: "conc_004",
  //   options: [
  //     "Monday",
  //     "Tuesday",
  //     "Wednesday",
  //     "Thrusday",
  //     "Friday",
  //     "Saturday",
  //     "Sunday",
  //     "Monday Thru Friday",
  //   ],
  //   category: "CONCLUSION",
  //   type: "single_choice",
  // },
  // {
  //   question:
  //     "We will contact you in approximately one month for a follow up call. Do you have a preferred contact time?",
  //   questionrandomid: "conc_005",
  //   options: [
  //     "8 am - 10 am",
  //     "10 am - 12 pm",
  //     "12 pm - 2pm",
  //     "2 pm - 4 pm",
  //     "4 pm - 6 pm",
  //     "Anytime between 8 am - 6 pm",
  //   ],
  //   category: "CONCLUSION",
  //   type: "single_choice",
  // },
];

export const groupQuestionsByCategory = (arr: any, chronicArr: any) => {
  // let chronic = allChronic
  //   .filter((v) => chronicArr.includes(v.diseaseName))
  //   .map((v) => v.questions)
  //   .flat();
  // let updatedArr = [...arr, ...chronic];
  let updatedArr = [...arr];

  console.log({ updatedArr, chronicArr });
  let existing = Object.values(
    updatedArr.reduce((acc: any, cur: any) => {
      (acc[cur.category] ||= []).push(cur);
      return acc;
    }, {}),
  ).map((group: any) => ({
    heading: group[0].category,
    questions: group,
  }));

  const chronicPrepared = (chronicArr ?? []).map((q: any, idx: number) => ({
    ...q,
    questionrandomid: q?.questionrandomid || `chronic_${idx + 1}`,
    category: "Chronic Conditions",
  }));

  const chronicIcdKey = (q: any): string => {
    if (q.icdCodePart != null && String(q.icdCodePart).trim() !== "") {
      return String(q.icdCodePart).trim();
    }
    if (typeof q.icdCode === "string" && q.icdCode.includes(" - ")) {
      return q.icdCode.split(" - ")[0].trim();
    }
    if (typeof q.icdCode === "string" && q.icdCode.trim() !== "") {
      return q.icdCode.trim();
    }
    return "Other";
  };

  const chronicByIcd = chronicPrepared.reduce((acc: Map<string, any[]>, q: any) => {
    const key = chronicIcdKey(q);
    if (!acc.has(key)) acc.set(key, []);
    acc.get(key)!.push(q);
    return acc;
  }, new Map<string, any[]>());

  const chronicSections: {
    heading: string;
    icdCodePart?: string;
    icdSectionTitle?: string;
    questions: any[];
  }[] =
    chronicPrepared.length === 0
      ? [{ heading: "Chronic Conditions", questions: [] }]
      : (Array.from(chronicByIcd.entries()) as [string, any[]][])
          .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
          .map(([icdPart, questions]) => ({
            heading: "Chronic Conditions",
            icdCodePart: icdPart,
            icdSectionTitle: questions[0]?.icdCode || icdPart,
            questions,
          }));

  existing = [...existing, ...chronicSections];

  let ordered = CalegoryFlow.flatMap((category: string) => {
    if (category === "Chronic Conditions") {
      return existing.filter((group: any) => group.heading === "Chronic Conditions");
    }
    const found = existing.find((group: any) => group.heading === category);
    return found ? [found] : [];
  });

  return ordered.filter(Boolean);
};


export const groupQuestionsByCategoryTreatmentPlan = (arr: any, chronicArr: any) => {
  // let chronic = allChronic
  //   .filter((v) => chronicArr.includes(v.diseaseName))
  //   .map((v) => v.questions)
  //   .flat();
  // let updatedArr = [...arr, ...chronic];
  let updatedArr = [...arr];

  console.log({ updatedArr, chronicArr });
  let existing = Object.values(
    updatedArr.reduce((acc: any, cur: any) => {
      (acc[cur.category] ||= []).push(cur);
      return acc;
    }, {}),
  ).map((group: any) => ({
    heading: group[0].category,
    questions: group,
  }));

  // if chronic questions are present, add them to the existing array else it will show blank card
  if(chronicArr &&  Array.isArray(chronicArr) && chronicArr?.length > 0 ){
    existing = [...existing, { heading: "Questions", questions: chronicArr.map((q: any, idx: number) => ({ ...q, questionrandomid: q?.questionrandomid || `chronic_${idx + 1}`, category: "Questions" })) }];
  }
  // let updated = [...existing, {heading : ""}]
  //  existing[existing.length-1]
  let ordered = swapLastTwo(existing);
  // return ordered;
  return ordered;
};



function swapLastTwo(arr: any) {
  if (arr.length < 2) return arr;
  const n = arr.length;
  const copy = [...arr];
  [copy[n - 2], copy[n - 1]] = [copy[n - 1], copy[n - 2]];
  return copy;
}

export function trimmedObject(obj: any) {
  try {
    let trimmedObj: any = {};
    Object.keys(obj).forEach((key, idx) => {
      let val = obj[key];
      if (typeof val == "string") {
        trimmedObj[key] = val.trim();
      } else {
        trimmedObj[key] = val;
      }
    });
    return trimmedObj;
  } catch (error) {
    return obj;
  }
}


export const checkOptOutcondition = (patient: any) => {
  return true;
}


export function excludeKeys(obj: any, keysToExclude: any) {
  if (!obj || typeof obj !== 'object') {
    return {};
  }
  if (!Array.isArray(keysToExclude)) {
    return { ...obj };
  }
  return Object.fromEntries(
    Object.entries(obj).filter(([key]) => !keysToExclude.includes(key))
  );
}

export const STATUS_BADGES = [
  // Existing
  {
    name: "ccm",
    mainColor: "#34C759",       // green
    bgColor: "#E8F9EE",         // light green
  },
  {
    name: "pcm",
    mainColor: "#34C759",       // green
    bgColor: "#E8F9EE",         // light green
  },
  {
    name: "rpm",
    mainColor: "#3A6DFF",       // blue
    bgColor: "#E7EEFF",         // light blue
  },

  {
    name: "Inactive",
    mainColor: "#FF3B30",
    bgColor: "#FFE5E5",
  },
  {
    name: "closed",
    mainColor: "#FF3B30",
    bgColor: "#FFE5E5",
  },
  {
    name: "Rejected",
    mainColor: "#FF3B30",
    bgColor: "#FFE5E5",
  },
  {
    name: "Active",
    mainColor: "#34C759",
    bgColor: "#E8F9EE",
  },
  {
    name: "Approved",
    mainColor: "#34C759",
    bgColor: "#E8F9EE",
  },
  {
    name: "Newly Added",
    mainColor: "#0F1C60",
    bgColor: "#E7EAF7",
  },
  {
    name: "Denied Service",
    mainColor: "#FF9500",
    bgColor: "#FFF3E2",
  },
  {
    name: "denied",
    mainColor: "#FF9500",
    bgColor: "#FFF3E2",
  },
  {
    name: "Opt Out",
    mainColor: "#FF9500",
    bgColor: "#FFF3E2",
  },
  {
    name: "opt out",
    mainColor: "#FF9500",
    bgColor: "#FFF3E2",
  },
  {
    name: "Archive",
    mainColor: "#FFCC00",
    bgColor: "#FFF9E3",
  },
  {
    name: "Pending",
    mainColor: "#FFCC00",
    bgColor: "#FFF9E3",
  },
  {
    name: "scheduled",
    mainColor: "#3A6DFF",
    bgColor: "#E7EEFF",
  },
  {
    name: "rescheduled",
    mainColor: "#FFCC00",
    bgColor: "#FFF9E3",
  },
  {
    name: "cancelled",
    mainColor: "#FF3B30",
    bgColor: "#FFE5E5",
  },
  {
    name: "canceled",
    mainColor: "#FF3B30",
    bgColor: "#FFE5E5",
  },
  {
    name: "completed",
    mainColor: "#34C759",
    bgColor: "#E8F9EE",
  },
  // Alerts / triage statuses
  {
    name: "Resolved",
    mainColor: "#34C759",
    bgColor: "#E8F9EE",
  },
  {
    name: "resolved",
    mainColor: "#34C759",
    bgColor: "#E8F9EE",
  },
  {
    name: "Invalid",
    mainColor: "#FF3B30",
    bgColor: "#FFE5E5",
  },
  {
    name: "invalid",
    mainColor: "#FF3B30",
    bgColor: "#FFE5E5",
  },
  {
    name: "Snoozed",
    mainColor: "#3A6DFF",
    bgColor: "#E7EEFF",
  },
  {
    name: "snoozed",
    mainColor: "#3A6DFF",
    bgColor: "#E7EEFF",
  },
  {
    name: "Pending Review",
    mainColor: "#FF9500",
    bgColor: "#FFF3E2",
  },
  {
    name: "pending_review",
    mainColor: "#FF9500",
    bgColor: "#FFF3E2",
  },
  {
    name: "Dismissed",
    mainColor: "#808080",
    bgColor: "#E5E5E5",
  },
  {
    name: "dismissed",
    mainColor: "#808080",
    bgColor: "#E5E5E5",
  },
  {
    name: "Added",
    mainColor: "#34C759",       // green
    bgColor: "#E8F9EE",         // light green
  },
  {
    name: "open",
    mainColor: "#34C759",       // green
    bgColor: "#E8F9EE",         // light green

  },
  {
    name: "Follow Up",
    mainColor: "#3A6DFF",       // blue
    bgColor: "#E7EEFF",         // light blue
  },
  {
    name: "followUp",
    mainColor: "#3A6DFF",       // blue
    bgColor: "#E7EEFF",         // light blue
  },

  // Patient types
  {
    name: "Outpatient",
    mainColor: "#3CB371",
    bgColor: "#E7F5EF",
  },
  {
    name: "Facility",
    mainColor: "#3A6DFF",
    bgColor: "#E7EEFF",
  },
  {
    name: "YES",
    mainColor: "#34C759",
    bgColor: "#E8F9EE",
  },
  {
    name: "NO",
    mainColor: "#808080",
    bgColor: "#E5E5E5",
  },
  {
    name: "Incoming",
    mainColor: "#3CB371",
    bgColor: "#E7F5EF",
  },
  {
    name: "Outgoing",
    mainColor: "#3A6DFF",
    bgColor: "#E7EEFF",
  },

  // ------------------------------------------------------
  // 👇 Newly Added as you requested
  // ------------------------------------------------------

  // Pending already exists above — no change needed

  {
    name: "Added",     // key: "added"
    mainColor: "#34C759",  // Green → completed/success
    bgColor: "#E8F9EE",
  },
  {
    name: "Follow up",     // key: "followUp"
    mainColor: "#3A6DFF",  // Blue → reminder/info
    bgColor: "#E7EEFF",
  },
  {
    name: "Followup",     // key: "followUp"
    mainColor: "#3A6DFF",  // Blue → reminder/info
    bgColor: "#E7EEFF",
  },
];

/** Human-readable plan status for CCM / treatment follow-up badges (e.g. followUp → Follow Up). */
export function formatPlanStatusLabel(raw: string): string {
  const spaced = raw
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim();
  if (!spaced) return "";
  return spaced
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

/** Patient list table status column (API `status` → display label). */
export function formatPatientListStatus(raw: unknown): string {
  const key = String(raw ?? "").trim().toLowerCase();
  if (key === "denied" || key === "opt_out" || key === "optout") return "Opt Out";
  if (!key) return "";
  return formatPlanStatusLabel(key);
}

export function getStatusBadge(value="") {
  if (!value) {
    return {
      name: "",
      mainColor: "#808080",
      bgColor: "#E5E5E5",
    };
  }

  // Convert value to string to ensure toLowerCase() works
  const valueStr = String(value || "");

  const found = STATUS_BADGES.find(
    (item) => item.name?.toLowerCase() === valueStr?.toLowerCase()
  );

  return (
    found || {
      name: valueStr,
      mainColor: "#808080",   // grey
      bgColor: "#E5E5E5",     // light grey
    }
  );
}


function getCareplanRenderedChoiceOptions(question: any): any[] {
  const base = Array.isArray(question?.options) ? question.options : [];
  const asString = (opt: any) =>
    typeof opt === "string" ? opt : String(opt?.value ?? opt?.label ?? "");
  const baseValues = base.map(asString);

  if (question?.type === "single_choice") {
    const hasYes = baseValues.some((o: string) => o.toLowerCase().includes("yes"));
    const hasNo = baseValues.some((o: string) => o.toLowerCase().includes("no"));
    return [...(hasYes ? [] : ["Yes"]), ...(hasNo ? [] : ["No"]), ...base];
  }

  if (question?.type === "multiple_choice") {
    const hasOther = baseValues.some((o: string) => o.toLowerCase().includes("other"));
    return [...base, ...(hasOther ? [] : ["Other"])];
  }

  return base;
}

export const getCareplanValues = (
  questions: any,
  renderedQuestions?: any[],
) => {
  const renderedById: Record<string, any> = {};
  if (Array.isArray(renderedQuestions)) {
    for (const q of renderedQuestions) {
      if (q?.questionrandomid) {
        renderedById[String(q.questionrandomid)] = q;
      }
    }
  }

  let obj: any = {};
  (questions && Array.isArray(questions) ? questions : []).forEach((q: any) => {
    const rendered = renderedById[String(q.questionrandomid)];
    const options = rendered
      ? getCareplanRenderedChoiceOptions(rendered)
      : q.options;
    obj[q.questionrandomid] = matchCareplanAnswerToOptions(q.answer, options);
    if (q.comment) {
      obj[`${q.questionrandomid}_comment`] = q.comment;
    }
  });
  return obj;
};

function matchCareplanAnswerToOptions(answer: any, options: any[]) {
  if (answer == null || answer === "") return answer;
  if (!Array.isArray(options) || !options.length) return answer;

  const optionValues = options.map((o) =>
    typeof o === "string" ? o : String(o?.value ?? o?.label ?? ""),
  );

  const resolve = (val: string) => {
    const answerTrimmed = val.trim();
    const match = optionValues.find((o) => o.trim() === answerTrimmed);
    return match ?? answerTrimmed;
  };

  if (Array.isArray(answer)) {
    return answer.map((a) => resolve(String(a)));
  }
  return resolve(String(answer));
}

/**
 * Formats a date as MM/DD/YYYY
 * @param date - Date object, string, or number (timestamp)
 * @returns Formatted date string in MM/DD/YYYY format
 */

export function formatDate(date: Date | string | number | null | undefined): string {
  if (!date) return "";

  // If backend sends ISO-like string (e.g. 1992-02-03T00:00:00.000Z),
  // take the calendar parts directly and show as MM-DD-YYYY.
  if (typeof date === "string") {
    const match = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, year, month, day] = match;
      return `${month}-${day}-${year}`;
    }
    // Non-ISO strings: return as-is
    return date;
  }

  const m = moment(date);

  if (!m.isValid()) return "";

  // Fallback for Date/number values
  return m.format("MM-DD-YYYY");
}

/**
 * Formats a date-time as MM/DD/YYYY HH:MM AM/PM
 * @param date - Date object, string, or number (timestamp)
 * @returns Formatted date-time string
 */
export function formatDateTime(date: Date | string | number | null | undefined): string {
  if (!date) return '';

  const m = moment(date);

  if (!m.isValid()) return '';

  // Convert to user's local timezone automatically
  const local = m.local();

  // Format exactly like your previous output
  return local.format("MM/DD/YYYY hh:mm A");
}


export function formatDateTimeNew(date: Date | string | number | null | undefined): string {
  // if (!date) return '';
  // const m = moment(date);
  // if (!m.isValid()) return '';
  // const local = m.local();
  // return local.format("MM/DD/YYYY hh:mm A");
  const d= date?.toString().split("T") || [];
  const arr= d[0]?.split("-") || [];
  const month = arr[1] || "";
  const day = arr[2] || "";
  const year = arr[0] || "";
  return `${month}/${day}/${year}`;
}

/**
 * Formats a date-time as MM/DD/YYYY HH:mm (24-hour format)
 * @param date - Date object, string, or number (timestamp)
 * @returns Formatted date-time string in 24-hour format
 */
export function formatDateTime24New(date: Date | string | number | null | undefined): string {
  if (!date) return '';

  const m = moment(date).format("HH:mm");
  console.log(date);

  // Format as MM/DD/YYYY HH:mm (24-hour format)

  const d= date?.toString().split("T") || [];
  const arr= d[0]?.split("-") || [];
  const month = arr[1] || "";
  const day = arr[2] || "";
  const year = arr[0] || "";
  console.log(d, arr, month, day, year);
  return `${month}/${day}/${year} ${m}`;

  // return local.format("MM/DD/YYYY HH:mm");
}

export function formatDateTime24(date: Date | string | number | null | undefined): string {
  if (!date) return '';

  const m = moment(date);

  if (!m.isValid()) return '';

  // Convert to user's local timezone automatically
  const local = m.local();

  // Format as MM/DD/YYYY HH:mm (24-hour format)
  return local.format("MM/DD/YYYY HH:mm");
}

/**
 * Formats a date-time as MM/DD/YYYY hh:mm A (12-hour format with AM/PM)
 * @param date - Date object, string, or number (timestamp)
 * @returns Formatted date-time string in 12-hour format with AM/PM
 */
export function formatDateTime12(date: Date | string | number | null | undefined): string {
  if (!date) return '';

  const m = moment(date);

  if (!m.isValid()) return '';

  // Convert to user's local timezone automatically
  const local = m.local();

  // Format as MM/DD/YYYY hh:mm A (12-hour format with AM/PM)
  return local.format("MM/DD/YYYY hh:mm A");
}

/**
 * Human-readable timezone for listings (IANA id → en-US CLDR via Intl).
 * Example: America/New_York → "Eastern Time (ET)" (varies with DST for *Generic* names).
 */
export function formatOrganizationTimezoneDisplay(
  iana: string | null | undefined,
): string {
  if (!iana || typeof iana !== "string" || !iana.trim()) return "—";
  const tz = iana.trim();
  try {
    const sample = new Date();
    const longGeneric = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "longGeneric",
    })
      .formatToParts(sample)
      .find((p) => p.type === "timeZoneName")?.value;
    const shortGeneric = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "shortGeneric",
    })
      .formatToParts(sample)
      .find((p) => p.type === "timeZoneName")?.value;
    if (longGeneric && shortGeneric && longGeneric !== shortGeneric) {
      return `${longGeneric} (${shortGeneric})`;
    }
    return longGeneric || shortGeneric || tz;
  } catch {
    return tz;
  }
}

/** Outpatient → org IANA; facility patient → facility IANA; otherwise "—". */
export function getPatientContextTimezoneDisplay(patient: unknown): string {
  const p = patient as Record<string, unknown> | null | undefined;
  if (!p || typeof p !== "object") return "—";
  const orgId = p.organizationId as Record<string, unknown> | undefined;
  const org = p.organization as Record<string, unknown> | undefined;
  const orgIana =
    (orgId?.timezone as string | undefined) ??
    (org?.timezone as string | undefined) ??
    (orgId?.timeZone as string | undefined) ??
    (org?.timeZone as string | undefined);
  const rawFacility = p.facilityId;
  const facility =
    rawFacility && typeof rawFacility === "object"
      ? (rawFacility as Record<string, unknown>)
      : null;
  const facilityIana =
    (facility?.timezone as string | undefined) ??
    (facility?.timeZone as string | undefined);
  if (p.typeOfPatient === "facility") {
    return formatOrganizationTimezoneDisplay(facilityIana);
  }
  if (p.typeOfPatient === "outpatient") {
    return formatOrganizationTimezoneDisplay(orgIana);
  }
  return "—";
}

export function formatUSPhoneNumber(phone: string): string {
  if (!phone || typeof phone !== 'string') return ""
  if (phone.substring(0, 2) !== '+1') return phone
  if (!phone) return "";

  // Remove everything except digits
  const digits = phone.replace(/\D/g, "");

  // Remove leading 1 if present (US country code)
  const cleaned = digits.startsWith("1") ? digits.substring(1) : digits;

  if (cleaned.length !== 10) return phone; // invalid → return as is

  const area = cleaned.substring(0, 3);
  const prefix = cleaned.substring(3, 6);
  const line = cleaned.substring(6);

  return `+1 (${area}) ${prefix}-${line}`;
}


export function splitIcdCode(str: string = ""): [string, string] {
  if (!str) return ["", ""];

  const index = str.indexOf("-");

  if (index === -1) {
    // If no "-" found, whole string is code
    return [str.trim(), ""];
  }

  const code = str.slice(0, index).trim();
  const description = str.slice(index + 1).trim();

  return [code, description];
}


export const getCurrentMonthYear = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const year = now.getFullYear();
  return `${month}-${year}`;
};

/** Preset period for org reports (enrollment + critical). */
export type ReportDatePreset =
  | "today"
  | "yesterday"
  | "last7"
  | "last15"
  | "last30"
  | "last90"
  | "custom";

function toYmdLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function startOfTodayLocal(): Date {
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth(), n.getDate());
}

/** Inclusive local date range for report APIs (`startDate` / `endDate` as YYYY-MM-DD). */
export function getReportDateRange(
  preset: ReportDatePreset,
  customFrom?: Date | null,
  customTo?: Date | null,
): { startDate: string; endDate: string } {
  const today = startOfTodayLocal();

  if (preset === "custom" && customFrom && customTo) {
    const a =
      customFrom.getTime() <= customTo.getTime() ? customFrom : customTo;
    const b =
      customFrom.getTime() <= customTo.getTime() ? customTo : customFrom;
    return { startDate: toYmdLocal(a), endDate: toYmdLocal(b) };
  }

  if (preset === "custom") {
    return getReportDateRange("last30");
  }
  switch (preset) {
    case "today":
      return { startDate: toYmdLocal(today), endDate: toYmdLocal(today) };
    case "yesterday": {
      const y = new Date(today);
      y.setDate(y.getDate() - 1);
      return { startDate: toYmdLocal(y), endDate: toYmdLocal(y) };
    }
    case "last7": {
      const s = new Date(today);
      s.setDate(s.getDate() - 6);
      return { startDate: toYmdLocal(s), endDate: toYmdLocal(today) };
    }
    case "last15": {
      const s = new Date(today);
      s.setDate(s.getDate() - 14);
      return { startDate: toYmdLocal(s), endDate: toYmdLocal(today) };
    }
    case "last30": {
      const s = new Date(today);
      s.setDate(s.getDate() - 29);
      return { startDate: toYmdLocal(s), endDate: toYmdLocal(today) };
    }
    case "last90": {
      const s = new Date(today);
      s.setDate(s.getDate() - 89);
      return { startDate: toYmdLocal(s), endDate: toYmdLocal(today) };
    }
    default:
      return getReportDateRange("last30");
  }
}





export const treatmentPlanQuestions = [
  // multiple_choice, single_choice , single_input, textarea,  multi_input , single_select,  multi_select
  {
    question:
      "Please verify that the patient has been informed verbally of the following information:",
    questionrandomid: "cons_001",
    options: [
      "There could be a copay (cost-sharing)",
      "You have the right to refuse/revoke enrollment at any time",
      "Your Plan of Care can be shared with your other providers",
      "Only one provider per month can provide these services to you",
    ],
    category: "CONSENT",
    type: "multiple_choice",
  },


  {
    question:
      "Your provider selected the following vital signs to track: (select all that apply)",
    questionrandomid: "general_001",
    options: [
      "Heart Rate",
      "Blood Pressure",
      "Weight",
      "Glucose (Blood Sugar)",
      "Oxygen",
      "Unable to answer/nonverbal",
      "Cognitively impaired",
    ],
    category: "GENERAL",
    type: "multiple_choice",
  },

  {
    question:
      "Your provider has set the following parameters: (select all that apply)",
    questionrandomid: "general_002",
    options: [
      "Blood pressure: Greater than 160/100 mmhg OR less than 90/60 mmhg",
      "Heart Rate: Greater than 100 BPM OR less than 60 BPM",
      "Blood Sugar: Greater than 350 mg/dl OR less than 60 mg/dl (Random & Fasting)",
      "Oxygen: Less than or equal to 90%",
      "Weight: Greater than 3lb weight gain within 24 hours or Greater than 3lb weight loss within 24 hours",
      "Unable to answer/nonverbal",
      "Cognitively impaired",
    ],
    category: "GENERAL",
    type: "multiple_choice",
  },

  {
    question: "Blood pressure: Greater than ___/___mmhg OR less than ___/___ mmhg",
    questionrandomid: "general_003",
    options: [],
    category: "GENERAL",
    type: "textarea",
  },

  {
    question: "Heart Rate: Greater than ____BPM OR less than ___ BPM",
    questionrandomid: "general_004",
    options: [],
    category: "GENERAL",
    type: "textarea",
  },





  {
    question: "Blood Sugar: Greater than ____ mg/dl OR less than ___ mg/dl (Random & Fasting)",
    questionrandomid: "general_005",
    options: [],
    category: "GENERAL",
    type: "textarea",
  },
  {
    question: "Oxygen: Less than or equal to ____%",
    questionrandomid: "general_006",
    options: [],
    category: "GENERAL",
    type: "textarea",
  },
  {
    question: "Weight: Greater than ____lb weight gain within 24 hours OR Greater than ____lb weight loss within 24 hours",
    questionrandomid: "general_007",
    options: [],
    category: "GENERAL",
    type: "textarea",
  },


    {
    question: "What type of enrollment?",
    questionrandomid: "enrollment_001",
    options: ["In-office", "In-home/facility","Remote"],
    category: "ENROLLMENT",
    type: "single_choice",
  },

  {
    question: "What date did you receive your equipment from Anthro Care?",
    questionrandomid: "enrollment_002",
    options: [],
    category: "ENROLLMENT",
    type: "date",
  },

  {
    question: " What date did you receive your educational training from Anthro Care?",
    questionrandomid: "enrollment_003",
    options: [],
    category: "ENROLLMENT",
    type: "date",
  },





  

  // {
  //   question: "Do you have any new allergies?",
  //   questionrandomid: "medh_001",
  //   options: [],
  //   category: "MEDICAL HISTORY",
  //   type: "textarea",
  // },
  // {
  //   question: "Have you had any recent procedures or surgeries?",
  //   questionrandomid: "medh_002",
  //   options: [],
  //   category: "MEDICAL HISTORY",
  //   type: "textarea",
  // },
  // {
  //   question: "Have you had any recent changes to your medications?",
  //   questionrandomid: "meds_001",
  //   options: ["YES", "NO"],
  //   category: "MEDICATIONS",
  //   type: "single_choice",
  // },
  // {
  //   question: "Do you take your medications as prescribed?",
  //   questionrandomid: "meds_002",
  //   options: ["YES", "NO"],
  //   category: "MEDICATIONS",
  //   type: "single_choice",
  // },
  // {
  //   question: "Do you need any medication refills?",
  //   questionrandomid: "meds_003",
  //   options: ["YES", "NO"],
  //   category: "MEDICATIONS",
  //   type: "single_choice",
  // },
  // {
  //   question:
  //     "Currently, how are you doing? Do you have any acute issues you would like to discuss with me today?",
  //   questionrandomid: "curr_001",
  //   options: [],
  //   category: "CURRENT STATUS",
  //   type: "textarea",
  // },
  // {
  //   question: "Do you need any lab work?",
  //   questionrandomid: "curr_002",
  //   options: ["YES", "NO"],
  //   category: "CURRENT STATUS",
  //   type: "single_choice",
  // },
  // {
  //   question: "Do you need any referrals?",
  //   questionrandomid: "curr_003",
  //   options: ["YES", "NO"],
  //   category: "CURRENT STATUS",
  //   type: "single_choice",
  // },
  // {
  //   question:
  //     "Do you need any help scheduling an appointment with your provider? I'd be more than happy to assist you with this now",
  //   questionrandomid: "curr_004",
  //   options: ["YES", "NO"],
  //   category: "CURRENT STATUS",
  //   type: "single_choice",
  // },
  // {
  //   question:
  //     "Do you have a good support system? Please let me know of any resources (besides your health care team) you can turn to for help with any health related problems (ex: family, friend, neighbors, religious/spiritual leaders, community center)”",
  //   questionrandomid: "curr_005",
  //   options: [],
  //   category: "CURRENT STATUS",
  //   type: "textarea",
  // },
  // {
  //   question: "Do you live in a safe environment?",
  //   questionrandomid: "curr_006",
  //   options: ["YES", "NO"],
  //   category: "CURRENT STATUS",
  //   type: "single_choice",
  // },

  // {
  //   question: "The patient has agreed to the plan of care.",
  //   questionrandomid: "conc_001",
  //   options: ["YES", "NO"],
  //   category: "CONCLUSION",
  //   type: "single_choice",
  // },
  // {
  //   question:
  //     "Are you aware that your healthcare team will have access to your plan of care, 24/7?",
  //   questionrandomid: "conc_002",
  //   options: ["YES", "NO"],
  //   category: "CONCLUSION",
  //   type: "single_choice",
  // },
  // {
  //   question:
  //     "Are you aware that you have access to Care Management Services, 24/7?",
  //   questionrandomid: "conc_003",
  //   options: ["YES", "NO"],
  //   category: "CONCLUSION",
  //   type: "single_choice",
  // },
  // // {
  // //   question:
  // //     "We will contact you in approximately one month for a follow up call. Do you have a preferred contact day?",
  // //   questionrandomid: "conc_004",
  // //   options: [
  // //     "Monday",
  // //     "Tuesday",
  // //     "Wednesday",
  // //     "Thrusday",
  // //     "Friday",
  // //     "Saturday",
  // //     "Sunday",
  // //     "Monday Thru Friday",
  // //   ],
  // //   category: "CONCLUSION",
  // //   type: "single_choice",
  // // },
  // {
  //   question:
  //     "We will contact you in approximately one month for a follow up call. Do you have a preferred contact time?",
  //   questionrandomid: "conc_005",
  //   options: [
  //     "8 am - 10 am",
  //     "10 am - 12 pm",
  //     "12 pm - 2pm",
  //     "2 pm - 4 pm",
  //     "4 pm - 6 pm",
  //     "Anytime between 8 am - 6 pm",
  //   ],
  //   category: "CONCLUSION",
  //   type: "single_choice",
  // },
];